import * as React from 'react';
import { Button, Modal, Popover, Radio, Row, Spin } from 'antd';
import { FC, useState } from 'react';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { compareStrings, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { deleteCapexCategoryAssociation } from '../../thunks/capex_association_thunks';
import { deleteRmCategoryAssociation } from '../../thunks/rm_association_thunks';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import CapExAdditionalInfoPopup from '../invoices_detail_details_page/CapExAdditionalInfoPopup';
import ExpenseCategoryAdditionalInfoPopup from '../invoices_detail_details_page/ExpenseCategoryAdditionalInfoPopup';
import {
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
} from '../../thunks/work_orders_thunks';
import {
	approvalRulesRestCrudThunksForBuyer,
	approvalRulesRestCrudThunksForSupplier,
	hasApprovalHierarchyChanged,
} from '../../thunks/company_approval_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { QUOTE_STATUSES } from '../../constants/QuoteStatuses';

interface CapexSwitchProps {
	quote: any;
	refresh: any;
	companyConfig: any;
	currentUser: any;

	updateWorkOrderPartially: any;
	deleteCapExAssociation: any;
	deleteRmAssociation: any;
	fetchBuyerCompanyApprovalSettings: any;
	getApprovalHierarchy: any;
	willApprovalHierarchyChange: any;
}

const CapexSwitch: FC<CapexSwitchProps> = ({
	quote,
	refresh,
	companyConfig,
	updateWorkOrderPartially,
	deleteCapExAssociation,
	deleteRmAssociation,
	willApprovalHierarchyChange,
}) => {
	const [expenseTypeLoading, setExpenseTypeLoading] = useState(false);
	const [categoryLoading, setCategoryLoading] = useState(false);
	const [capExAdditionalInfoVisible, setCapExAdditionalInfoVisible] = useState(false);
	const [rmAdditionalInfoVisible, setRmAdditionalInfoVisible] = useState(false);
	const [buttonLoading, setButtonLoading] = useState(false);
	const isCapex = nullSafeGetOrElse('workOrder.isCapex', quote, false);
	const refreshQuote = () => refresh();

	const updateIsCapexInWorkOrder = (isCapex) => {
		const needUpdate =
			(quote.workOrder.isCapex && !isCapex) || (!quote.workOrder.isCapex && isCapex);
		if (needUpdate) {
			setExpenseTypeLoading(true);
			updateWorkOrderPartially(nullSafeGet('workOrder.id', quote), { isCapex })
				.then(() => refreshQuote())
				.finally(() => setExpenseTypeLoading(false));
		}
	};
	const deleteCapexCategoryAssociationHandler = () => {
		if (quote.capExCategoryAssociationId) {
			Promise.all([
				deleteCapExAssociation(quote.capExCategoryAssociationId),
				updateIsCapexInWorkOrder(false),
			]);
		} else {
			updateIsCapexInWorkOrder(false);
		}
	};
	const deleteRmCategoryAssociationHandler = () => {
		if (quote.rmCategoryAssociationId) {
			Promise.all([
				deleteRmAssociation(quote.rmCategoryAssociationId),
				updateIsCapexInWorkOrder(true),
			]);
		} else {
			updateIsCapexInWorkOrder(true);
		}
	};
	const showCapExAdditionalInfo = () => setCapExAdditionalInfoVisible(true);
	const showRmAdditionalInfo = () => setRmAdditionalInfoVisible(true);
	const isCapexCategoryEnabled = () => {
		return nullSafeGetOrElse('config.capExConfig.acceptCapExCategory', companyConfig, false);
	};
	const isRmCategoryEnabled = () => {
		return nullSafeGetOrElse('config.rmConfig.acceptRmCategory', companyConfig, false);
	};
	const changeExpenseType = (value) => {
		if (value) {
			// Switching to CapEx
			if (isRmCategoryEnabled() && quote.rmCategoryAssociationId) {
				deleteRmAssociation(quote.rmCategoryAssociationId);
			}
			if (isCapexCategoryEnabled()) {
				showCapExAdditionalInfo();
			} else {
				updateIsCapexInWorkOrder(true);
			}
		} else {
			// Switching to R&M
			if (isCapexCategoryEnabled() && quote.capExCategoryAssociationId) {
				deleteCapExAssociation(quote.capExCategoryAssociationId);
			}
			if (isRmCategoryEnabled()) {
				showRmAdditionalInfo();
			} else {
				updateIsCapexInWorkOrder(false);
			}
		}
	};
	const onChangeExpenseType = (visible, value) => {
		setButtonLoading(true);

		willApprovalHierarchyChange(nullSafeGet('workOrder.id', quote), { isCapex: value }).then(
			(result) => {
				const quoteChanged =
					nullSafeGet('quoteApprovalChanges.hasApprovedByUsers', result) &&
					nullSafeGetOrElse('quoteApprovalChanges.changedHierarchy.length', result, 0) > 0;
				const invoiceChanged =
					nullSafeGet('invoiceApprovalChanges.hasApprovedByUsers', result) &&
					nullSafeGetOrElse('invoiceApprovalChanges.changedHierarchy.length', result, 0) > 0;
				if (quoteChanged || invoiceChanged) {
					const message = compareStrings(nullSafeGet('status', quote), QUOTE_STATUSES.approved)
						? 'This quote has been approved, so this change will not affect the approval hierarchy.'
						: 'This will change the approval hierarchy';
					Modal.confirm({
						title: message,
						content: 'Do you want to proceed?',
						onOk() {
							setButtonLoading(false);
							changeExpenseType(value);
						},
						onCancel() {
							setButtonLoading(false);
						},
					});
				} else {
					setButtonLoading(false);
					changeExpenseType(value);
				}
			}
		);
	};
	const hideCapExAdditionalInfo = () => {
		setCapExAdditionalInfoVisible(false);
		updateIsCapexInWorkOrder(true);
	};
	const hideRmAdditionalInfo = () => {
		setRmAdditionalInfoVisible(false);
		updateIsCapexInWorkOrder(false);
	};
	const capExCategoryAssociationSuccess = () => {
		hideCapExAdditionalInfo();
		if (quote.workOrder.isCapex) {
			setCategoryLoading(true);
			Promise.resolve(refreshQuote()).finally(() => setCategoryLoading(false));
		}
	};
	const rmCategoryAssociationSuccess = () => {
		hideRmAdditionalInfo();
		if (!quote.workOrder.isCapex) {
			setCategoryLoading(true);
			Promise.resolve(refreshQuote()).finally(() => setCategoryLoading(false));
		}
	};

	return (
		<div className="flex flex-col content-start justify-start">
			{capExAdditionalInfoVisible && (
				<CapExAdditionalInfoPopup
					workOrder={quote.workOrder}
					onCancel={hideCapExAdditionalInfo}
					onSuccess={capExCategoryAssociationSuccess}
				/>
			)}
			{rmAdditionalInfoVisible && (
				<ExpenseCategoryAdditionalInfoPopup
					categoryType="rm"
					workOrder={quote.workOrder}
					onCancel={hideRmAdditionalInfo}
					onSuccess={rmCategoryAssociationSuccess}
				/>
			)}
			<div className="mr-3 flex flex-row items-center gap-2.5">
				<Spin spinning={expenseTypeLoading || buttonLoading}>
					<Radio.Group value={isCapex} style={{ display: 'flex' }}>
						{isCapex === false ? (
							<Radio.Button
								style={{ flexWrap: 'nowrap' }}
								value={false}
								onClick={() => {}}
								disabled={expenseTypeLoading || buttonLoading}
							>
								R&M
							</Radio.Button>
						) : (
							<Radio.Button
								style={{ flexWrap: 'nowrap' }}
								value={false}
								disabled={expenseTypeLoading || buttonLoading}
								onClick={() => {
									onChangeExpenseType(true, false);
								}}
							>
								R&M
							</Radio.Button>
						)}
						{isCapex === true ? (
							<Radio.Button
								style={{ flexWrap: 'nowrap' }}
								value={true}
								onClick={() => {}}
								disabled={expenseTypeLoading || buttonLoading}
							>
								CapEx
							</Radio.Button>
						) : (
							<Radio.Button
								onClick={() => {
									onChangeExpenseType(true, true);
								}}
								style={{ flexWrap: 'nowrap' }}
								value={true}
							>
								CapEx
							</Radio.Button>
						)}
					</Radio.Group>
				</Spin>
				<Popover
					content={
						<div>
							<Row>
								<span style={{ fontSize: '20px' }}>Capital Expenditures (CapEx)</span>
							</Row>
							<Row>
								<span>Costs associated with buying new assets or upgrading existing assets</span>
							</Row>
							<Row style={{ height: '20px' }}></Row>
							<Row>
								<span style={{ fontSize: '20px' }}>Repair and Maintenance (R&M)</span>
							</Row>
							<Row>
								<span>Costs associated with repair and maintenance of existing assets</span>
							</Row>
						</div>
					}
					placement={'topRight'}
				>
					<InfoCircleOutlined />
				</Popover>
			</div>
			{isCapex && isCapexCategoryEnabled() && (
				<Spin spinning={categoryLoading}>
					<div className="mt-2 flex flex-row items-center justify-end">
						<span style={{ color: 'rgba(0, 0, 0, 0.45' }}>CapEx Category: </span>
						<div style={{ color: 'rgba(0, 0, 0, 0.65' }} className="ml-2">
							{quote.capExCategory || '--'}
						</div>
						<Button
							type="link"
							size="large"
							icon={<EditOutlined onClick={showCapExAdditionalInfo} />}
						/>
					</div>
				</Spin>
			)}
			{!isCapex && isRmCategoryEnabled() && (
				<Spin spinning={categoryLoading}>
					<div className="mt-2 flex flex-row items-center justify-end">
						<span style={{ color: 'rgba(0, 0, 0, 0.45' }}>R&M Category: </span>
						<div style={{ color: 'rgba(0, 0, 0, 0.65' }} className="ml-2">
							{quote.rmCategory || '--'}
						</div>
						<Button
							type="link"
							size="large"
							icon={<EditOutlined />}
							onClick={showRmAdditionalInfo}
						/>
					</div>
				</Spin>
			)}
		</div>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	willApprovalHierarchyChange: (workOrderId, params) =>
		dispatch(hasApprovalHierarchyChanged(ownProps.userType, workOrderId, params)),
	fetchBuyerCompanyApprovalSettings: () =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? approvalRulesRestCrudThunksForSupplier.read(
						{ approvalHierarchyType: 'proposal' },
						'approvalRulesIndex'
				  )
				: approvalRulesRestCrudThunksForBuyer.read(
						{ approvalHierarchyType: 'proposal' },
						'approvalRulesIndex'
				  )
		),
	updateWorkOrderPartially: (id, entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrdersRestCrudThunksForSupplier.updatePartially(id, entity)
				: workOrdersRestCrudThunksForBuyer.updatePartially(id, entity)
		),
	deleteCapExAssociation: (id) => dispatch(deleteCapexCategoryAssociation(ownProps.userType)(id)),
	deleteRmAssociation: (id) => dispatch(deleteRmCategoryAssociation(ownProps.userType)(id)),
});

const ComponentWithoutUserType = withRouter(connect(null, mapDispatchToProps)(CapexSwitch));

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
